import React, { useState, useEffect } from "react";
import {
  FiUsers,
  FiStar,
  FiTruck,
  FiClipboard,
  FiImage,
  FiVolume2,
  FiPlayCircle,
  FiFilm,
  FiAlertTriangle,
} from "react-icons/fi";
import { supabase } from '../services/supabase';

const iconMap = {
  FiUsers: <FiUsers />,
  FiStar: <FiStar />,
  FiTruck: <FiTruck />,
  FiClipboard: <FiClipboard />,
  FiImage: <FiImage />,
  FiVolume2: <FiVolume2 />,
  FiPlayCircle: <FiPlayCircle />,
  FiFilm: <FiFilm />,
  FiAlertTriangle: <FiAlertTriangle />,
};

export default function Competences() {
  const [data, setData] = useState({
    apropos: {
      title: '',
      hero: { title: '', description: '' },
      items: []
    },
    competences: {
      title: '',
      items: []
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCompetencesData();
  }, []);

  const fetchCompetencesData = async () => {
    try {
      const { data: competencesData, error } = await supabase
        .from('competences')
        .select('*')
        .single();

      if (error) {
        console.error('Supabase error:', error);
        setError('Failed to load content');
        return;
      }

      if (competencesData) {
        setData({
          apropos: competencesData.apropos || { title: '', hero: { title: '', description: '' }, items: [] },
          competences: competencesData.competences || { title: '', items: [] }
        });
      }
    } catch (error) {
      console.error('Error fetching competences data:', error);
      setError('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="competences loading">Loading...</div>;
  }

  if (error) {
    return <div className="competences error">Error: {error}</div>;
  }

  return (
    <>
      {/* À PROPOS section */}
      {data.apropos.title && (
        <section className="apropos" aria-labelledby="apropos-title">
          <div className="container">
            <h2 id="apropos-title">{data.apropos.title}</h2>

            {data.apropos.hero.title && (
              <div className="apropos-hero" role="region" aria-labelledby="presentation-title">
                <div className="apropos-hero-inner">
                  <h3 id="presentation-title">{data.apropos.hero.title}</h3>
                  <p>{data.apropos.hero.description}</p>
                </div>
              </div>
            )}

            {data.apropos.items.length > 0 && (
              <div className="apropos-grid">
                {data.apropos.items.map((item) => (
                  <div key={item.id} className="apropos-item">
                    <h3>{item.title}</h3>
                    {item.content && <p>{item.content}</p>}
                    {item.items && item.items.length > 0 && (
                      <ul>
                        {item.items.map((listItem, index) => (
                          <li key={index}>{listItem}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Competences section */}
      {data.competences.title && (
        <section className="competences" aria-labelledby="competences-title">
          <div className="container">
            <h2 id="competences-title">{data.competences.title}</h2>

            {data.competences.items.length > 0 && (
              <div className="competences-cards" role="list">
                {data.competences.items.map((item) => (
                  <article className="card" role="listitem" key={item.id}>
                    <div className="card-icon">{iconMap[item.icon] || <FiUsers />}</div>
                    <p className="card-text">{item.text}</p>
                  </article>
                ))}
              </div>
            )}
          </div>

          {/* Replaced/updated styles: add apropos-hero styles and minor spacing tweaks */}
          <style>{`
            :root{
              --accent: #c20e0f;
              --bg-soft: #fafafa;
              --muted: #6b6b6b;
              --card-bg: #ffffff;
              --max-w: 1100px;
              --radius: 12px;
              --gap: 1rem;
            }

            /* Prevent horizontal scroll */
            * {
              box-sizing: border-box;
            }

            /* Apropos */
            .apropos{
              background: linear-gradient(180deg, #fff 0%, var(--bg-soft) 100%);
              padding: clamp(1.5rem, 4vw, 2.25rem) clamp(0.75rem, 3vw, 1rem);
              color: #222;
              width: 100%;
              overflow-x: hidden;
            }
            .apropos .container{ 
              max-width: var(--max-w); 
              width: 100%;
              margin: 0 auto;
              padding: 0 clamp(0.5rem, 2vw, 1rem);
            }
            .apropos h2{
              margin: 0 0 clamp(0.625rem, 2vw, 0.75rem) 0;
              font-size: clamp(1.25rem, 3.5vw, 1.5rem);
              text-align: center;
              letter-spacing: 0.2px;
              position: relative;
            }
            .apropos h2::after{
              content: "";
              display: block;
              width: clamp(48px, 10vw, 56px);
              height: 4px;
              background: var(--accent);
              margin: clamp(0.5rem, 2vw, 0.625rem) auto 0;
              opacity: 0.95;
            }

            /* NEW: hero card for the Présentation (centered, prominent) */
            .apropos-hero{
              display: flex;
              justify-content: center;
              margin: clamp(0.75rem, 2.5vw, 1rem) 0;
              width: 100%;
            }
            .apropos-hero-inner{
              width: 100%;
              max-width: min(100%, 820px);
              background: linear-gradient(90deg, rgba(194,14,15,0.03), rgba(255,255,255,0.02));
              padding: clamp(0.875rem, 2.5vw, 1.1rem) clamp(1rem, 3vw, 1.25rem);
             
              border-left: clamp(4px, 1vw, 6px) solid rgba(194,14,15,0.12);
              text-align: center;
            }
            .apropos-hero-inner h3{
              margin: 0 0 clamp(0.3rem, 1vw, 0.35rem) 0;
              font-size: clamp(1rem, 2.5vw, 1.06rem);
              color: #111;
              line-height: 1.3;
            }
            .apropos-hero-inner p{
              margin: 0;
              color: var(--muted);
              line-height: 1.6;
              font-size: clamp(0.9rem, 2.2vw, 0.98rem);
            }

            .apropos-grid{
              display: grid;
              grid-template-columns: 1fr;
              gap: clamp(0.875rem, 2.5vw, 1rem);
              margin-top: clamp(0.875rem, 2.5vw, 1rem);
              width: 100%;
            }

            .apropos-item{
              background: var(--card-bg);
              padding: clamp(0.875rem, 2.5vw, 1rem) clamp(1rem, 3vw, 1.125rem);
            
              border-left: 4px solid rgba(194,14,15,0.06);
              transition: transform 220ms ease, box-shadow 220ms ease;
              width: 100%;
            }
            .apropos-item:hover,
            .apropos-item:focus-within{
              transform: translateY(-6px);
              
            }
            .apropos-item h3{ 
              margin: 0 0 clamp(0.35rem, 1.5vw, 0.4rem) 0; 
              font-size: clamp(0.9rem, 2.3vw, 0.98rem); 
              color: #111;
              line-height: 1.3;
            }
            .apropos-item p{ 
              margin: 0; 
              color: var(--muted); 
              line-height: 1.45; 
              font-size: clamp(0.875rem, 2.1vw, 0.95rem);
            }
            .apropos-item ul{ 
              margin: 0; 
              padding-left: clamp(1rem, 2.5vw, 1.1rem); 
              color: var(--muted);
            }
            .apropos-item ul li{ 
              margin: clamp(0.3rem, 1vw, 0.35rem) 0; 
              list-style: disc;
              font-size: clamp(0.875rem, 2.1vw, 0.95rem);
              line-height: 1.5;
            }

            /* Competences (cards) */
            .competences{ 
              padding: clamp(1.5rem, 4vw, 2rem) clamp(0.75rem, 3vw, 1rem);
              width: 100%;
              overflow-x: hidden;
            }
            .competences .container{ 
              max-width: var(--max-w); 
              width: 100%;
              margin: 0 auto;
              padding: 0 clamp(0.5rem, 2vw, 1rem);
            }
            .competences h2{ 
              margin-bottom: clamp(0.875rem, 2.5vw, 1rem); 
              font-size: clamp(1.25rem, 3.5vw, 1.5rem); 
              text-align: center;
            }

            .competences-cards{
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(min(100%, 220px), 1fr));
              gap: clamp(0.875rem, 2.5vw, 1rem);
              margin-top: clamp(0.875rem, 2.5vw, 1rem);
              width: 100%;
            }

            .card{
              background: var(--card-bg);
              padding: clamp(0.875rem, 2.5vw, 1rem);
              display: flex;
              gap: clamp(0.625rem, 2vw, 0.9rem);
              align-items: center;
              min-height: clamp(72px, 15vw, 88px);
            
              transition: transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease;
              border: 1px solid rgba(16,24,40,0.04);
              width: 100%;
            }
            .card:hover,
            .card:focus-within{
              transform: translateY(-8px);
              
              border-color: rgba(194,14,15,0.08);
            }

            .card-icon{
              width: clamp(44px, 10vw, 52px);
              height: clamp(44px, 10vw, 52px);
              flex: 0 0 clamp(44px, 10vw, 52px);
              border-radius: 50%;
              background: rgba(194,14,15,0.08);
              color: var(--accent);
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: clamp(1.1rem, 2.8vw, 1.3rem);
           
            }

            .card-text{
              margin: 0;
              font-size: clamp(0.9rem, 2.2vw, 0.98rem);
              line-height: 1.25;
              color: #111;
              flex: 1;
            }

            /* Tablet */
            @media(min-width: 768px){
              .apropos-grid{ 
                grid-template-columns: repeat(2, 1fr); 
                gap: 1.25rem;
              }
            }

            /* Small Laptop */
            @media (max-width: 1024px) {
              .apropos-grid {
                gap: 1rem;
              }
              
              .competences-cards {
                grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
              }
            }

            /* Tablet */
            @media (max-width: 768px) {
              .apropos {
                padding: 1.75rem 0.875rem;
              }
              
              .competences {
                padding: 1.75rem 0.875rem;
              }
              
              .apropos-grid {
                grid-template-columns: 1fr;
                gap: 1rem;
              }
              
              .competences-cards {
                grid-template-columns: 1fr;
                gap: 0.875rem;
              }
              
              .card {
                min-height: 80px;
              }
              
              .apropos-hero-inner {
                padding: 1rem;
              }
            }

            /* Mobile */
            @media (max-width: 480px) {
              .apropos {
                padding: 1.5rem 0.75rem;
              }
              
              .competences {
                padding: 1.5rem 0.75rem;
              }
              
              .apropos .container,
              .competences .container {
                padding: 0 0.5rem;
              }
              
              .apropos h2, 
              .competences h2 { 
                font-size: 1.25rem;
                margin-bottom: 0.75rem;
              }
              
              .apropos h2::after {
                width: 48px;
                margin-top: 0.5rem;
              }
              
              .apropos-hero {
                margin: 0.75rem 0;
              }
              
              .apropos-hero-inner {
                padding: 0.875rem 1rem;
                border-left-width: 4px;
              }
              
              .apropos-hero-inner h3 {
                font-size: 1rem;
                margin-bottom: 0.3rem;
              }
              
              .apropos-hero-inner p {
                font-size: 0.9rem;
                line-height: 1.5;
              }
              
              .apropos-grid {
                gap: 0.875rem;
                margin-top: 0.875rem;
              }
              
              .apropos-item { 
                padding: 0.875rem 1rem;
               
              }
              
              .apropos-item:hover,
              .apropos-item:focus-within {
                transform: translateY(-4px);
              }
              
              .apropos-item h3 {
                font-size: 0.9rem;
              }
              
              .apropos-item p,
              .apropos-item ul li {
                font-size: 0.875rem;
              }
              
              .apropos-item ul {
                padding-left: 1rem;
              }
              
              .competences-cards {
                grid-template-columns: 1fr;
                gap: 0.75rem;
                margin-top: 0.875rem;
              }
              
              .card { 
                padding: 0.75rem;
                gap: 0.625rem;
                min-height: 72px;
               
              }
              
              .card:hover,
              .card:focus-within {
                transform: translateY(-4px);
              }
              
              .card-icon { 
                width: 44px;
                height: 44px;
                font-size: 1.1rem;
                flex: 0 0 44px;
              }
              
              .card-text { 
                font-size: 0.9rem;
                line-height: 1.3;
              }
            }

            /* Desktop - Fine-tune for large screens */
            @media (min-width: 1200px) {
              .apropos {
                padding: 2.5rem 1rem;
              }
              
              .competences {
                padding: 2.5rem 1rem;
              }
              
              .apropos-grid {
                gap: 1.5rem;
              }
              
              .competences-cards {
                grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
                gap: 1.25rem;
              }
            }
          `}</style>
        </section>
      )}
    </>
  );
}
